import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { formatCurrency } from 'core-lib/utils/currency';
import { calculateEMI } from '../../utils/calculate';
import s from './CalculatorWidget.module.scss';
import { RangeSlider, Typography } from 'ui';
import { Color, TypeScales } from 'ui/primitives';

type WidgetType = {
  amount?: number;
  rate?: number;
  tenure?: number;
};

export function CalculatorWidget({ amount, rate, tenure }: WidgetType) {
  const [config, setConfig] = useState({
    amount: 5000000,
    rate: 9,
    tenure: 20,
  });

  const [calculatedValues, setCalculatedValues] = useState({
    monthlyEmi: 0,
    principal: 0,
    interest: 0,
    total: 0,
    percent: 0,
  });

  useEffect(() => {
    const tempValues = JSON.parse(JSON.stringify(config));
    if (amount) {
      tempValues.amount = amount;
    }
    if (rate) {
      tempValues.rate = rate;
    }
    if (tenure) {
      tempValues.tenure = tenure;
    }
    setConfig(tempValues);
  }, [amount, rate, tenure]);

  useEffect(() => {
    const result = calculateEMI({
      loanAmt: config.amount,
      interest: config.rate,
      tenure: config.tenure,
    });
    setCalculatedValues({
      monthlyEmi: result.monthlyAmt || 0,
      principal: config.amount || 0,
      interest: result.totalPaid - config.amount || 0,
      total: result.totalPaid || 0,
      percent:
        ((result.totalPaid - config.amount) / result.totalPaid) * 100 || 0,
    });
  }, [config.amount, config.rate, config.tenure]);

  return (
    <div className={s.root}>
      <h2 className={s.heading}>EMI calculator</h2>
      <div className={s.graph_value}>
        <div className={s.left}>
          <div className={s.slider_wrapper}>
            <div className={s.top}>
              <p className={s.key}>Enter loan amount</p>
              <p className={s.value}>{formatCurrency(config.amount)}</p>
            </div>
            <div className={s.slider}>
              <RangeSlider
                value={config.amount}
                onChange={(val) => {
                  setConfig((state) => ({ ...state, amount: val }));
                }}
                min={100000}
                minLabel=""
                maxLabel=""
                step={20000}
                max={10000000}
              />
            </div>
            <div className={s.range_slider_min_max_display}>
              <Typography
                label={'₹0'}
                variant={TypeScales.body_para_r}
                color={Color.text_subheading}
              />
              <Typography label={'₹1 Cr'} color={Color.text_subheading} />
            </div>
          </div>
          <div className={s.slider_wrapper}>
            <div className={s.top}>
              <p className={s.key}>Enter interest rate (p.a)</p>
              <p className={s.value}>{config.rate}%</p>
            </div>
            <div className={s.slider}>
              <RangeSlider
                value={config.rate}
                onChange={(val) => {
                  setConfig((state) => ({ ...state, rate: val }));
                }}
                min={1}
                minLabel=""
                maxLabel=""
                step={0.05}
                max={30}
              />
            </div>
            <div className={s.range_slider_min_max_display}>
              <Typography
                label={'0.05%'}
                variant={TypeScales.body_para_r}
                color={Color.text_subheading}
              />
              <Typography label={'30%'} color={Color.text_subheading} />
            </div>
          </div>
          <div className={s.slider_wrapper}>
            <div className={s.top}>
              <p className={s.key}>Select period of tenure</p>
              <p className={s.value}>{config.tenure} Yr</p>
            </div>
            <div className={s.slider}>
              <RangeSlider
                value={config.tenure}
                onChange={(val) => {
                  setConfig((state) => ({ ...state, tenure: val }));
                }}
                min={1}
                minLabel=""
                maxLabel=""
                step={0.5}
                max={30}
              />
            </div>
            <div className={s.range_slider_min_max_display}>
              <Typography
                label={'0.5 year'}
                variant={TypeScales.body_para_r}
                color={Color.text_subheading}
              />
              <Typography label={'30 year'} color={Color.text_subheading} />
            </div>
          </div>
        </div>
        <div className={s.right}>
          <div className={s.graph}>
            <div
              className={cn(s.pie, s.animate, s.no_round)}
              //@ts-ignore
              style={{ '--p': `${calculatedValues.percent}`, '--c': '#7AD9D3' }}
            />
            <div className={s.white_center}>
              <Typography
                label={'Monthly EMI :'}
                variant={TypeScales.body_para_r}
                color={Color.text_subheading}
              />
              <Typography
                label={formatCurrency(calculatedValues.monthlyEmi)}
                variant={TypeScales.h1}
              />
            </div>
          </div>
          <div className={s.type}>
            <div className={s.item_1}>
              <div className={s.box} />
              <div className={s.chart_indicators}><p>Principal amount</p>
              <Typography
                variant={TypeScales.h3}
                label={formatCurrency(calculatedValues.principal)}
              /></div>
            
            </div>
            <div className={s.item_2}>
              <div className={s.box} />
              <div className={s.chart_indicators}><p>Interest amount</p>
              <Typography
                variant={TypeScales.h3}
                label={formatCurrency(calculatedValues.interest)}
              /></div>
            </div>
          </div>
        </div>
      </div>
      <div className={s.bottom}>
        <div className={s.items}>
          <p className={s.key}>Monthly EMI</p>
          <p className={s.value}>
            {formatCurrency(calculatedValues.monthlyEmi)}
          </p>
        </div>
        <div className={s.items}>
          <p className={s.key}>Principal amount</p>
          <p className={s.value}>
            {formatCurrency(calculatedValues.principal)}
          </p>
        </div>
        <div className={s.items}>
          <p className={s.key}>Total interest</p>
          <p className={s.value}>{formatCurrency(calculatedValues.interest)}</p>
        </div>
      </div>
      <div className={s.total}>
        <p>Total amount</p>
        <p>{formatCurrency(calculatedValues.total)}</p>
      </div>
    </div>
  );
}
